import {
  OPENAI_VALUES,
  modelSupportsInputFidelity,
  formatSupportsCompression,
  type OpenAiImageParams,
} from "../lib/books-api";
import { AdvancedSection } from "@/components/layout";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

// ─── Modellparameter von POST /v1/images/edits ───────────────────────────────
// Geteilt von allen Karten, die diesen Endpoint konfigurieren: Preview- und
// Szenen-Generierung (pro Buch, BookDetail) und Character-Sheet (global,
// Einstellungen). Die Dropdowns tragen exakt die Werte, die die API akzeptiert
// (OPENAI_VALUES, gegen die Live-API verifiziert) — ein ungültiger Wert kann
// also gar nicht erst gespeichert werden. "(nicht senden)" lässt den Parameter
// weg, dann gilt der API-Default. Alles, was hier nicht modelliert ist, geht
// über „Weitere Parameter".

/** Label des "Parameter weglassen"-Eintrags in jedem Dropdown. */
export const EMPTY_LABEL = "nicht senden (API-Standard)";

// Radix-Select erlaubt keinen leeren Wert; "" wird intern auf diesen Platzhalter abgebildet.
const NONE = "__none__";

export function extraToText(extra: Record<string, string>): string {
  return Object.entries(extra)
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");
}
export function textToExtra(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf("=");
    if (eq <= 0) continue;
    out[trimmed.slice(0, eq).trim()] = trimmed.slice(eq + 1).trim();
  }
  return out;
}

type SelectKey =
  "quality" | "background" | "inputFidelity" | "outputFormat" | "moderation";

/**
 * Alle Modellparameter von POST /v1/images/edits. Modell, Größe und Qualität
 * sind immer sichtbar; der Rest (inkl. „Weitere Parameter“, wenn `extra`
 * übergeben wird) steht unter „Erweitert“. `idPrefix` hält die IDs eindeutig,
 * wenn mehrere Karten gleichzeitig auf der Seite stehen.
 */
export function OpenAiParamFields({
  value,
  onChange,
  idPrefix,
  extra,
  onExtraChange,
}: {
  value: OpenAiImageParams;
  onChange: (patch: Partial<OpenAiImageParams>) => void;
  idPrefix: string;
  extra?: string;
  onExtraChange?: (text: string) => void;
}) {
  const fidelityOk = modelSupportsInputFidelity(value.model);
  const compressionOk = formatSupportsCompression(value.outputFormat);

  /** Dropdown über die Werte, die die API tatsächlich akzeptiert. */
  const select = (
    label: string,
    key: SelectKey,
    hint?: string,
    disabled = false,
  ) => (
    <Field key={key} data-disabled={disabled}>
      <FieldLabel htmlFor={`${idPrefix}-${key}`}>{label}</FieldLabel>
      <Select
        disabled={disabled}
        value={value[key] === "" ? NONE : value[key]}
        onValueChange={(v) =>
          onChange({ [key]: v === NONE ? "" : v } as Partial<OpenAiImageParams>)
        }
      >
        <SelectTrigger id={`${idPrefix}-${key}`} className="w-full bg-card">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {OPENAI_VALUES[key].map((v) => (
            <SelectItem key={v || NONE} value={v === "" ? NONE : v}>
              {v === "" ? EMPTY_LABEL : v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <FieldDescription>{hint}</FieldDescription>}
    </Field>
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field>
          <FieldLabel htmlFor={`${idPrefix}-model`}>Modell</FieldLabel>
          <Input
            id={`${idPrefix}-model`}
            list={`${idPrefix}-model-options`}
            value={value.model}
            className="bg-card"
            onChange={(e) => onChange({ model: e.target.value })}
          />
          <datalist id={`${idPrefix}-model-options`}>
            {OPENAI_VALUES.model.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </Field>
        <Field>
          <FieldLabel htmlFor={`${idPrefix}-size`}>Größe</FieldLabel>
          <Input
            id={`${idPrefix}-size`}
            list={`${idPrefix}-size-options`}
            value={value.size}
            className="bg-card"
            onChange={(e) => onChange({ size: e.target.value })}
          />
          <datalist id={`${idPrefix}-size-options`}>
            {OPENAI_VALUES.size.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
          <FieldDescription>auto oder BREITExHÖHE</FieldDescription>
        </Field>
        {select("Qualität", "quality", "steuert Detailgrad und Kosten")}
      </div>

      <AdvancedSection>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            {select(
              "Input Fidelity",
              "inputFidelity",
              fidelityOk
                ? "high = mehr Detail aus den Referenzbildern"
                : `von ${value.model} nicht unterstützt – wird nicht gesendet`,
              !fidelityOk,
            )}
            {select("Hintergrund", "background")}
            {select("Ausgabeformat", "outputFormat")}
            <Field data-disabled={!compressionOk}>
              <FieldLabel htmlFor={`${idPrefix}-compression`}>
                Kompression
              </FieldLabel>
              <Input
                id={`${idPrefix}-compression`}
                type="number"
                min={0}
                max={100}
                disabled={!compressionOk}
                value={value.outputCompression}
                placeholder={EMPTY_LABEL}
                className="bg-card"
                onChange={(e) =>
                  onChange({ outputCompression: e.target.value })
                }
              />
              <FieldDescription>
                {compressionOk
                  ? "0–100"
                  : "nur bei jpeg/webp – wird nicht gesendet"}
              </FieldDescription>
            </Field>
            {select("Moderation", "moderation")}
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-n`}>
                Anzahl Bilder (n)
              </FieldLabel>
              <Input
                id={`${idPrefix}-n`}
                type="number"
                min={1}
                max={99}
                value={value.n}
                className="bg-card"
                onChange={(e) => onChange({ n: e.target.value })}
              />
              <FieldDescription>
                es wird das erste Bild benutzt
              </FieldDescription>
            </Field>
          </div>
          {extra !== undefined && onExtraChange && (
            <OpenAiExtraField
              value={extra}
              onChange={onExtraChange}
              idPrefix={idPrefix}
            />
          )}
        </div>
      </AdvancedSection>
    </div>
  );
}

/** Freitext-Passthrough für Parameter, die hier nicht modelliert sind. */
export function OpenAiExtraField({
  value,
  onChange,
  idPrefix = "openai",
}: {
  value: string;
  onChange: (text: string) => void;
  idPrefix?: string;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={`${idPrefix}-extra`}>Weitere Parameter</FieldLabel>
      <Textarea
        id={`${idPrefix}-extra`}
        rows={3}
        value={value}
        className="bg-card font-mono text-xs"
        placeholder={"stream=false\npartial_images=0\nuser=startales"}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldDescription>
        Ein <code>key=value</code> pro Zeile, wird ungeprüft mitgeschickt. Ein
        Name, den die API nicht kennt, lässt den ganzen Request mit{" "}
        <code>unknown_parameter</code> scheitern.
      </FieldDescription>
    </Field>
  );
}
